import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, 
  Button, Stack 
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n';
import { getIncidents } from './incidentsService';

export default function IncidentsListPage({ readOnly = false }) {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadIncidents = async () => {
      try {
        setLoading(true);
        const incidents = await getIncidents();
        setRows(incidents);
      } catch (error) {
        console.error('Error loading incidents:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadIncidents();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('common_loading') || 'Loading...'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">
          {readOnly 
            ? (t('incidents_archive_list_title') || 'Archived Incidents') 
            : (t('incidents_list_title') || 'Incidents List')
          }
        </Typography>
        
        {!readOnly && (
          <Button 
            variant="contained" 
            component={RouterLink}
            to="/incidents/create"
          >
            {t('incidents_create_btn') || 'Create Incident'}
          </Button>
        )}
      </Stack>

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('common_date')}</TableCell>
              <TableCell>{t('incidents_responsible')}</TableCell>
              <TableCell>{t('incidents_client')}</TableCell>
              <TableCell>{t('incidents_type')}</TableCell>
              <TableCell>{t('incidents_description')}</TableCell>
              <TableCell>{t('incidents_photos')}</TableCell>
              <TableCell align="right">{t('actions_actions') || 'Actions'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length > 0 ? rows.map(incident => (
              <TableRow key={incident.id}>
                <TableCell>{formatDate(incident.date)}</TableCell>
                <TableCell>{incident.responsible}</TableCell>
                <TableCell>{incident.clientName || incident.clientId || '-'}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      textTransform: 'capitalize',
                      color: getTypeColor(incident.type)
                    }}
                  >
                    {incident.type}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {incident.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  {Array.isArray(incident.photos) ? incident.photos.length : 0}
                </TableCell>
                <TableCell align="right">
                  <Button
                    component={RouterLink}
                    to={`/incidents/${incident.id}`}
                    size="small"
                    variant="outlined"
                  >
                    {t('actions_view') || 'View'}
                  </Button>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography color="text.secondary">
                    {t('incidents_no_rows') || 'No incidents found'}
                  </Typography>
                  {!readOnly && (
                    <Button 
                      component={RouterLink}
                      to="/incidents/create"
                      variant="outlined"
                      sx={{ mt: 1 }}
                    >
                      {t('incidents_create_btn') || 'Create First Incident'}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

function formatDate(dateString) {
  try {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString || '-';
  }
}

function getTypeColor(type) {
  const colors = {
    client: '#1976d2',
    technical: '#ed6c02', 
    logistics: '#2e7d32',
    safety: '#d32f2f'
  };
  return colors[type] || '#757575';
}

export { IncidentsListPage as IncidentsList };